import React, { useLayoutEffect, useRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import Topbar from 'src/components/topbar';
import { Container } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ExplorationBlob from 'src/assets/images/exploration_BLOB_1.png';
import MDUIButton from 'src/components/m3/button';
import {
  setPrintHtmlPart as setPrintHtmlPartAction,
  setPrintHtmlHead as setPrintHtmlHeadAction
} from 'src/store/actions';
import {
  extractPrintableSection,
  buildPrintHead,
  buildFullPrintHtml
} from 'src/utils/print.utils';
import html2pdf from 'html2pdf.js';
import { sendSlackNotification } from 'src/store/services';
import useStyles from './style';

function SolutionLayout({ children, showButton = false }) {
  const { printHtml } = useSelector(state => state.questionReducer);
  const { loading } = useSelector(state => state.businessCase);
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const hasAutoGeneratedRef = useRef(false);
  const solutionRef = useRef(null);
  const businessRef = useRef(null);

  // Auto-generate and send PDF via Slack when entering /business-case
  useLayoutEffect(() => {
    const autoGenerateAndSendPdf = async () => {
      if (pathname !== '/business-case' || hasAutoGeneratedRef.current) {
        return;
      }

      const el = businessRef.current;
      if (!el) return;

      hasAutoGeneratedRef.current = true;

      const html = extractPrintableSection(el);
      const partsFromState = { ...printHtml?.parts, business: html };
      const ordered = ['home', '1', '2', '3', '4', 'solution', 'business']
        .map(k => partsFromState[k])
        .filter(Boolean);

      if (!ordered.length) return;

      const full = buildFullPrintHtml(
        ordered,
        printHtml.head + buildPrintHead()
      );

      // Generate PDF blob
      const pdfBlob = await html2pdf()
        .from(full)
        .set({
          filename: 'business_case.pdf',
          html2canvas: { windowWidth: 1024, scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: {
            mode: ['avoid-all', 'css', 'legacy'],
            before: '.page-break',
            after: '.page-break'
          },
          margin: 5
        })
        .outputPdf('blob');

      // Send to Slack
      await sendSlackNotification(pdfBlob);
    };

    autoGenerateAndSendPdf();

    return () => {};
  }, [pathname, printHtml?.parts, printHtml?.head]);

  const handleBuisenessCaseClick = () => {
    // Capture solution section into print parts before navigating
    const el = solutionRef.current;
    if (el) {
      const html = extractPrintableSection(el);
      dispatch(setPrintHtmlPartAction('solution', html));
    }
    dispatch(setPrintHtmlHeadAction(buildPrintHead()));

    navigate('/business-case');
  };
  const handleGeneratePdf = async () => {
    const el = businessRef.current;
    const html = extractPrintableSection(el);
    const partsFromState = { ...printHtml?.parts, business: html };
    const ordered = ['home', '1', '2', '3', '4', 'solution', 'business']
      .map(k => partsFromState[k])
      .filter(Boolean);
    if (!ordered.length) return;

    const full = buildFullPrintHtml(ordered, printHtml.head + buildPrintHead());

    // Just save the PDF locally, don't send to Slack
    await html2pdf()
      .from(full)
      .set({
        filename: 'business_case.pdf',
        html2canvas: { windowWidth: 1024, scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break',
          after: '.page-break'
        },
        margin: 5
      })
      .save();
  };

  return (
    <>
      <Container style={{ position: 'relative' }}>
        <img
          src={ExplorationBlob}
          alt="Exploration blob design"
          className={classes.blobImage}
        />
      </Container>

      <Topbar />

      <Container className={classes.root}>
        <div className={classes.contentWrapper}>
          <div className={classes.mainContent}>
            {cloneElement(children, { solutionRef, businessRef })}
          </div>
        </div>

        {showButton && (
          <div className={classes.floatingButton}>
            {pathname === '/solution' ? (
              <MDUIButton
                className={classes.button}
                onClick={handleBuisenessCaseClick}
              >
                {t('solution.buisnessCase')}
              </MDUIButton>
            ) : (
              <MDUIButton
                className={classes.button}
                onClick={handleGeneratePdf}
                disabled={loading}
              >
                {t('common.print', 'Print PDF')}
              </MDUIButton>
            )}
          </div>
        )}
      </Container>
    </>
  );
}

SolutionLayout.propTypes = {
  children: PropTypes.node.isRequired,
  showButton: PropTypes.bool
};

export default SolutionLayout;
